import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { publicBlogList, publicBlogCategories } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ChevronRight, Clock, Eye, Search as SearchIcon, Newspaper, ArrowLeft } from 'lucide-react';

const SITE_LOGO = 'https://customer-assets.emergentagent.com/job_87cd0f70-4f18-4348-a2fd-e2666a61734d/artifacts/ozrawt0t_IMG_0439.jpeg';

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return iso.slice(0, 10); }
}

function PostCard({ post }) {
  return (
    <Link to={`/blog/${post.slug}`} className="group block" data-testid={`blog-card-${post.slug}`}>
      <Card className="card-surface overflow-hidden h-full transition-transform duration-200 group-hover:-translate-y-1">
        <div className="aspect-[16/9] bg-white/[0.04] overflow-hidden">
          {post.cover_image ? (
            <img src={post.cover_image} alt={post.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground">
              <Newspaper className="h-10 w-10 opacity-30" />
            </div>
          )}
        </div>
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em]">
            {post.category && (
              <span className="text-[hsl(43_92%_56%)] font-semibold">{post.category}</span>
            )}
            <span className="text-muted-foreground/60">·</span>
            <span className="text-muted-foreground">{fmtDate(post.published_at)}</span>
          </div>
          <h3 className="text-base font-bold leading-snug group-hover:text-[hsl(43_92%_60%)] transition-colors">
            {post.title}
          </h3>
          {post.summary && (
            <p className="text-xs text-muted-foreground line-clamp-3 leading-relaxed">{post.summary}</p>
          )}
          <div className="flex items-center gap-3 text-[11px] text-muted-foreground pt-1">
            {post.reading_minutes && (
              <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {post.reading_minutes} min</span>
            )}
            <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" /> {post.views || 0}</span>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

export default function BlogIndexPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(params.get('q') || '');

  const activeCategory = params.get('category') || '';

  useEffect(() => {
    publicBlogCategories().then((d) => setCategories(d.categories || [])).catch(() => { /* noop */ });
  }, []);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    publicBlogList({
      ...(activeCategory ? { category: activeCategory } : {}),
      ...(search.trim() ? { q: search.trim() } : {}),
      limit: 30,
    }).then((d) => {
      if (cancel) return;
      setItems(d.items || []);
      setTotal(d.total || 0);
    }).catch(() => { /* noop */ })
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [activeCategory, search]);

  function setCategory(c) {
    const next = new URLSearchParams(params);
    if (c) next.set('category', c); else next.delete('category');
    setParams(next, { replace: true });
  }

  const featured = items[0];
  const rest = items.slice(1);

  return (
    <div className="min-h-screen bg-[hsl(240_6%_6%)] text-white" data-testid="blog-index-page">
      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-md bg-[hsl(240_6%_6%)/0.85] border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group">
            <img src={SITE_LOGO} alt="AlveeTrade" className="h-8 w-8 rounded-sm" />
            <div className="leading-tight">
              <div className="font-display tracking-wider text-sm">ALVEETRADE</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(43_92%_56%)]">Blog</div>
            </div>
          </Link>
          <Button variant="outline" size="sm" onClick={() => navigate('/')}
            className="border-white/10 bg-white/[0.02] text-muted-foreground hover:text-white gap-2">
            <ArrowLeft className="h-3.5 w-3.5" /> Home
          </Button>
        </div>
      </header>

      {/* Hero / page title */}
      <section className="max-w-6xl mx-auto px-4 pt-10 pb-6">
        <h1 className="text-3xl sm:text-4xl font-bold leading-tight" data-testid="blog-index-title">
          AlveeTrade <span className="text-[hsl(43_92%_56%)]">Blog</span>
        </h1>
        <p className="mt-3 text-sm text-muted-foreground max-w-2xl">
          Trading education, broker research, regulation guides, and beginner-friendly strategies — written by traders, for traders.
        </p>
      </section>

      {/* Filters */}
      <section className="max-w-6xl mx-auto px-4 mb-8">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <SearchIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search articles…"
              className="pl-9 bg-white/[0.02] border-white/10"
              data-testid="blog-index-search" />
          </div>
          <button
            type="button" onClick={() => setCategory('')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              !activeCategory
                ? 'bg-[hsl(43_92%_56%)] text-black'
                : 'border border-white/10 bg-white/[0.02] text-muted-foreground hover:text-white'
            }`}
            data-testid="blog-cat-all"
          >All</button>
          {categories.filter((c) => c.count > 0).map((c) => (
            <button key={c.name} type="button" onClick={() => setCategory(c.name)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                activeCategory === c.name
                  ? 'bg-[hsl(43_92%_56%)] text-black'
                  : 'border border-white/10 bg-white/[0.02] text-muted-foreground hover:text-white'
              }`}
              data-testid={`blog-cat-${c.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {c.name} <span className="opacity-60">({c.count})</span>
            </button>
          ))}
        </div>
      </section>

      {/* List */}
      <section className="max-w-6xl mx-auto px-4 pb-16">
        {loading ? (
          <div className="text-center text-muted-foreground py-20 text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-center text-muted-foreground py-20 text-sm" data-testid="blog-empty">
            No posts published yet.
          </div>
        ) : (
          <>
            {featured && (
              <Link to={`/blog/${featured.slug}`} className="block mb-10 group" data-testid="blog-featured">
                <Card className="card-surface overflow-hidden">
                  <div className="grid md:grid-cols-2">
                    <div className="aspect-[16/10] md:aspect-auto bg-white/[0.04]">
                      {featured.cover_image ? (
                        <img src={featured.cover_image} alt={featured.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                          <Newspaper className="h-16 w-16 opacity-30" />
                        </div>
                      )}
                    </div>
                    <div className="p-6 flex flex-col justify-center space-y-3">
                      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em]">
                        {featured.category && <span className="text-[hsl(43_92%_56%)] font-semibold">{featured.category}</span>}
                        <span className="text-muted-foreground/60">·</span>
                        <span className="text-muted-foreground">{fmtDate(featured.published_at)}</span>
                      </div>
                      <h2 className="text-2xl sm:text-3xl font-bold leading-tight group-hover:text-[hsl(43_92%_60%)] transition-colors">
                        {featured.title}
                      </h2>
                      {featured.summary && <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">{featured.summary}</p>}
                      <div className="inline-flex items-center gap-1 text-xs text-[hsl(43_92%_56%)] font-medium group-hover:gap-2 transition-all">
                        Read article <ChevronRight className="h-3.5 w-3.5" />
                      </div>
                    </div>
                  </div>
                </Card>
              </Link>
            )}

            {rest.length > 0 && (
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {rest.map((p) => <PostCard key={p.id} post={p} />)}
              </div>
            )}

            {total > items.length && (
              <div className="text-center mt-10 text-xs text-muted-foreground">
                Showing {items.length} of {total} posts.
              </div>
            )}
          </>
        )}
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 mt-10">
        <div className="max-w-6xl mx-auto px-4 py-6 flex items-center justify-between flex-wrap gap-3 text-xs text-muted-foreground">
          <div>© {new Date().getFullYear()} AlveeTrade. All rights reserved.</div>
          <Link to="/" className="hover:text-white">← Back to alveetrade.com</Link>
        </div>
      </footer>
    </div>
  );
}
